import type ContentManagementInterface from '../../../models/ContentManagementInterface';
import type BackendService from '../../../services/admin/BackendService';
import type { ImageCardsCropMeta } from './useImageCardsSplit';

/**
 * Restores "Adjust crops" for any ImageCarousel or HeroImage block whose crop source is
 * still live (not yet auto-deleted) — best-effort, never blocks the content
 * load itself. Scoped to block ids this content actually has, so a crop
 * source left over from a block that's since been removed/replaced is
 * silently ignored rather than offering Adjust crops on nothing. Shared by the
 * Fund Opportunity and Fund Update editors, keyed by whichever `modelType` the
 * caller's content belongs to.
 */
export function restoreCardsAdjust(
  svc: BackendService,
  modelType: string,
  modelId: string,
  content: ContentManagementInterface,
  setCardsAdjustByBlockId: (
    updater: (prev: Record<string, ImageCardsCropMeta>) => Record<string, ImageCardsCropMeta>,
  ) => void,
): void {
  const blockIds = new Set(
    content.content
      .filter((b) => b.type === 'ImageCarousel' || b.type === 'HeroImage')
      .map((b) => b.id),
  );
  if (blockIds.size === 0) return;

  svc
    .listImageCardCropSources(modelType, modelId)
    .then((sources) => {
      const restored: Record<string, ImageCardsCropMeta> = {};
      for (const [blockId, row] of Object.entries(sources)) {
        if (!blockIds.has(blockId)) continue;
        restored[blockId] = {
          source: {
            url: row.sourceUrl,
            path: row.sourcePath,
            width: row.sourceWidth,
            height: row.sourceHeight,
          },
          boxes: row.boxes,
          confidence: 'high',
        };
      }
      if (Object.keys(restored).length > 0) {
        setCardsAdjustByBlockId((prev) => ({ ...prev, ...restored }));
      }
    })
    .catch((err) => console.warn('Could not restore Adjust crops state:', err));
}
